import { Navigate } from 'react-router-dom';

import { OMEGA_ADMIN_STORAGE_KEY } from '@/features/omega/adminMode';
import { OmegaShell, OmegaTopPage } from '@/features/omega/dashboard';
import { useIsFounder } from '@/hooks/useIsAdmin';

/**
 * Founder unlock token. Visiting `/omega?key=founder` once flips the
 * `gi-engine.omega-admin` localStorage flag to `"true"` so subsequent
 * visits (without the query param) keep working. The token is a
 * client-side UX hint — not a credential. Real auth is the signed
 * founder-key challenge on the server's `POST /omega/genesis` route.
 */
const FOUNDER_UNLOCK_TOKEN = 'founder';

/**
 * Side-effect on render: if the URL carries `?key=founder`, persist
 * the admin flag in localStorage. Idempotent — every render is safe
 * to call. Wrapped in try/catch so SecurityError (incognito quota,
 * locked-down environments) doesn't crash the render.
 */
function consumeFounderUnlockOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('key') === FOUNDER_UNLOCK_TOKEN) {
      window.localStorage.setItem(OMEGA_ADMIN_STORAGE_KEY, 'true');
    }
  } catch {
    // localStorage unavailable — fall through; gate stays closed.
  }
}

/**
 * Route entry for `/omega`. Gated by [`useIsFounder`] — strict
 * email-allowlist check. Non-founders redirect to / regardless of
 * any client-side flag (`?key=founder`, localStorage toggles, env
 * overrides). The route's existence is not leaked.
 *
 * The `?key=founder` URL param is a *convenience* unlock — visiting
 * `/omega?key=founder` once persists the legacy
 * `gi-engine.omega-admin` localStorage flag for tooling that still
 * reads it (e.g. Playwright fixtures, the legacy adminMode helper).
 * It is **not** a security boundary. The actual gate is the
 * authenticated user's email being on [`ADMIN_EMAILS`]. A non-founder
 * who guesses `/omega?key=founder` still gets bounced to /.
 *
 * Renders the dedicated `OmegaShell` per
 * `docs/OMEGA-DASHBOARD-SPEC.md` §4.1 — distinct from
 * `RegularUserShell`. The shell paints its own dark surface + top
 * bar over the surrounding regular-shell chrome it is currently
 * nested inside (Phase-1 compromise; L2/L3/L4 routes will be
 * promoted to a sibling top-level Outlet route in a follow-up).
 */
export function OmegaPage() {
  consumeFounderUnlockOnce();
  const isFounder = useIsFounder();
  if (!isFounder) {
    return <Navigate to="/" replace />;
  }
  return (
    <OmegaShell>
      <OmegaTopPage />
    </OmegaShell>
  );
}
